import queryString from 'query-string'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { set } from 'vue'
import { v4 as uuid } from 'uuid'
import { getDeviceFeature } from './utils/deviceFeature'
import type { FbRuntimeConfig } from '@/composables/fbq/useFbRuntimeConfig'
import { normalizeHttps, sleep } from '@/utils'
import { $getToken, $removeDeviceId, $removeToken, $setDeviceId, $setToken } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, STORAGE_READ_CHAPTER_LAST, STORAGE_TRACK_VALUE_CHANNEL_NAME_MAP, STORAGE_TRACK_VALUE_FIRST_ORIGIN_MAP, STORAGE_TRACK_VALUE_ORIGIN_NAME_MAP, isIOS } from '@/constants'

export const useEffectiveSubmitTrackStore = defineStore({
  id: 'effectiveSubmitTrack',
  state: () => ({
    isAttribution: false,
    params: {},
  }),
  actions: {
    $attribution() {
      this.$state.isAttribution = true
    },

    $unAttribution() {
      this.$state.isAttribution = false
    },

    $setParams(params: Record<string, any>) {
      this.$state.params = params
    },
  },
})

export const useLoadingStore = defineStore({
  id: 'loading',
  state: () => ({
    visible: false,
  }),
  actions: {
    $showLoading() {
      this.$state.visible = true
    },
    $hideLoading() {
      this.$state.visible = false
    },
  },
})

export const useToastStore = defineStore({
  id: 'toast',
  state: () => ({
    message: '',
  }),
  actions: {
    async $toast(message: string, duration = 1500) {
      this.message = message
      await sleep(duration)
      this.message = ''
    },
  },
})

export const useHistoryStore = defineStore({
  id: 'history',
  state: () => ({
    stack: [] as string[],
    prevPage: {},
  }),
  actions: {
    $push(url: string) {
      this.stack.push(url)
    },
    $replace(url: string) {
      set(this.$state.stack, Math.max(this.stack.length - 1, 0), url)
    },
    $pop() {
      const url = this.stack[this.stack.length - 2]
      set(this.$state, 'stack', this.stack.slice(0, -2))
      return url
    },
    $clear() {
      this.stack = []
    },
    $setPrevPage(title: string, origin: string, herf: string) {
      this.prevPage = {
        route_title: title,
        route_host: origin,
        route_url: herf,
      }
    },
  },
})

// Common query
export const useQueryStore = defineStore({
  id: 'query',
  state: () => ({
    max_pid: '',
    max_sid: '',
    max_app_id: '',
  }),
  actions: {
    init(query: Record<string, any>) {
      if (query.max_pid)
        this.max_pid = query.max_pid as string
      if (query.max_sid)
        this.max_sid = query.max_sid as string
      if (query.max_app_id)
        this.max_app_id = query.max_app_id as string
    },
    stringify() {
      return queryString.stringify({
        max_app_id: this.$state.max_app_id,
        max_pid: this.$state.max_pid,
        max_sid: this.$state.max_sid,
      })
    },
  },
})

interface ReadChapterLastData {
  contentId?: string
  chapterId?: string
  expire?: number
}

const duration = 1000 * 60 * 5 // 5min
export const useReadStore = defineStore({
  id: 'read',
  state: () => {
    let readChapterLast = {} as ReadChapterLastData
    try {
      readChapterLast = JSON.parse(localStorage.getItem(STORAGE_READ_CHAPTER_LAST) ?? '{}') as ReadChapterLastData
      if (readChapterLast.expire && readChapterLast.expire < Date.now())
        readChapterLast = {}
    }
    catch {}
    return {
      contentId: readChapterLast?.contentId ?? '',
      chapterId: readChapterLast?.chapterId ?? '',
    }
  },
  actions: {
    update({ contentId, chapterId }: { contentId: string; chapterId: string }) {
      if (!contentId || !chapterId)
        return
      this.contentId = contentId
      this.chapterId = chapterId
      localStorage.setItem(STORAGE_READ_CHAPTER_LAST, JSON.stringify({
        contentId,
        chapterId,
        expire: Date.now() + duration,
      }))
      setTimeout(() => {
        this.$reset()
      }, duration)
    },
  },
})

export const useConfigStore = defineStore({
  id: 'config',
  state: () => ({
    _fetched: false,
    appId: '',
    appName: '',
    appPackageId: '',
    appType: '',
    componentAppId: '',
    oauthDomain: '',
    payDomain: '',
    payAppId: '',
    forceAttentionMp: false,
    noPopup: false,
    mpAppId: '',
    wxAppName: '',
    subscribeTemplateList: [] as { templateId: string }[],
    strategy: null as any,
    alreadyGetStratrgy: false,
    // 自动续费 & 破冰
    renewVipState: false,
    renewIceBreakingState: false,
    renewIceBreakingStyleConfig: '1' as string | number,
  }),
  actions: {
    async init(options: { force: boolean } = { force: false }) {
      if (this.$state._fetched && !options.force)
        return
      const { data } = await api.get<null, GetAppConfigGetResponse>('/app/config/get')
      this.$state = { ...this.$state, ...data, _fetched: true } as any

      window.collectCommonParams = window.collectCommonParams ?? {}
      window.collectCommonParams = {
        ...window.collectCommonParams,
        os: isIOS ? 'ios' : 'andr',
        pline: '微信公众号',
        content_type: '长篇',
        wx_app_name: data.appName,
        wx_appid: data.mpAppId,
        partner_id: data.partnerId,
      }

      // Normalize some domains
      this.$state.oauthDomain = normalizeHttps(this.$state.oauthDomain)

      // Note: Update common query
      const queryStore = useQueryStore()
      queryStore.$patch({
        max_pid: data.pid,
        max_sid: data.sid,
        max_app_id: data.appId,
      })

      // Update document title
      if (this.$state.wxAppName)
        document.title = this.$state.wxAppName
    },
    updateStrategy(value: any) {
      this.$state.strategy = value
    },
    updateAlreadyGetStratrgy(state: boolean) {
      this.$state.alreadyGetStratrgy = !!state
    },
  },
})

export const useSpreadInfoStore = defineStore({
  id: 'spreadInfo',
  state: () => ({
    landingPageType: '',
    utm_campaign_id: '',
    utm_medium: '',
    utm_adset_id: '',
    utm_ad_id: '',
    pId: '',
    adDomainId: '',
    bizAdId: '',
    adChannel: '',
    fbRuntimeConfig: null as FbRuntimeConfig | null,
  }),
  actions: {
    $setSpreadInfo(data: any) {
      this.$patch(data)
    },
    $clearSpreadInfo() {
      this.$patch({
        landingPageType: '',
        utm_campaign_id: '',
        utm_medium: '',
        utm_adset_id: '',
        utm_ad_id: '',
        pId: '',
        adDomainId: '',
        bizAdId: '',
      })
    },
    $setFbRuntimeConfig(config: FbRuntimeConfig) {
      this.fbRuntimeConfig = config
    },
  },
})

export const useUserStore = defineStore({
  id: 'user',
  state: () => ({
    _fetched: false,
    appId: '',
    coinBalance: 0,
    code: '',
    memberId: '',
    memberName: '',
    headImageUrl: '',
    thirdPartId: '',
    thirdNickName: '',
    thirdHeadImgUrl: '',
    thirdPartType: '',
    vipEndTime: '',
    createDateTime: '',
    email: '',
    userSubscribeState: false,
    autoLock: false,
    subscribe: false,
    subscribeRenewal: false,
    subscribeEndDate: '',
    // 迁移小程序公告
    transfer: false,
    transferLink: '',
    rechargeCount: null, // 累计充值次数
    landingPageId: '', // 从广告来的用户
    landingPageContentId: '', // 投放的内容id
    subscribeType: 0, // 订阅类型
    bizAdId: '', // 自定义广告id, 避免facebook utm参数赋值失败
  }),
  actions: {
    isLogin() {
      return Boolean($getToken())
    },
    async login(deviceUuid: string) {
      const effectiveSubmitTrackStore = useEffectiveSubmitTrackStore()
      const deviceFeature = await getDeviceFeature()
      await api.post<null>('/member/login', {
        equipmentId: deviceUuid,
        attributionRequest: effectiveSubmitTrackStore.$state.params,
        deviceFeature,
      }).then(async (res: any) => {
        $setToken(res?.data?.token)
      })
    },
    async transferLogin(transferToken: string) {
      const diviceId = uuid()
      $setDeviceId(diviceId)

      await api.post<null>('/member/login', {
        transferToken,
        equipmentId: diviceId,
      }).then(async (res: any) => {
        $setToken(res?.data?.token)
      })
    },
    logout() {
      $removeToken()
      $removeDeviceId()
      // 清理听书播放器状态，避免切换账号后保留上一个用户的播放进度
      closeAudioPlayerOnLogout()
    },
    async init(options: { force: boolean; isNewUser?: boolean } = { force: false, isNewUser: false }) {
      if (this.$state._fetched && !options.force)
        return
      const [{ data }] = await Promise.all([
        api.get<null, GetMemberDetailGetResponse>('/member/detail/get').then((res) => {
          return res
        }),
      ])

      // 在 app 端被删除, 重新生成用户
      if (data.signOut) {
        await this.logout()
        const diviceId = uuid()
        $setDeviceId(diviceId)
        await this.login(diviceId)
      }

      this.$state = { ...this.$state, ...data, _fetched: true }
      const { $setSpreadInfo } = useSpreadInfoStore()
      $setSpreadInfo({
        landingPageType: data.landingPageType,
        utm_campaign_id: data.campaignId,
        utm_medium: data.medium,
        utm_adset_id: data.adSetId,
        utm_ad_id: data.adId,
        pId: data.PId,
        adDomainId: data.adDomainId,
        bizAdId: data.bizAdId,
        adChannel: data.adChannel,
      })
      if (data.PId) {
        import('@/composables/fbq/useFbRuntimeConfig').then(({ fetchFbRuntimeConfig }) => {
          fetchFbRuntimeConfig(data.PId as string)
        })
      }
      // 阿里数据跟踪 增加用户标识匹配
      if (window.RumSDK) {
        window.RumSDK.default.setConfig('user', {
          name: data.memberId,
        })
      }
      // clarity 数据跟踪 增加用户标识匹配
      if (window.clarity)
        window.clarity('identify', String(data.code))
    },
  },
})

export const useEmailLogin = defineStore({
  id: 'emailLogin',
  state: () => ({
    visible: false,
  }),
  actions: {
    $showLoginPopup() {
      this.$state.visible = true
    },
    $hideLoginPopup() {
      this.$state.visible = false
    },
    isEmailLogin() {
      const user = useUserStore()
      return Boolean(user.email)
    },
  },
})

export const useActivityStore = defineStore({
  id: 'activity',
  state: () => ({
    _fetched: false,
  } as { _fetched: boolean } & {
    [key in string]: {
      id: string
      name: string
      title: string
      startDate: string
      endDate: string
      activityTemplateId: string
      activityType: number
      productId: string
      limitTimes: number
      limitType: number
      toastPosition: number
      toastPositionList: null
      toastStat: number
    }
  }),
  actions: {
    async init() {
      if (this.$state._fetched)
        return
      try {
        const { data } = await api.get<null, GetActivityGettoastactivitymapResponse>('/activity/getToastActivityMap')
        this.$state = { ...this.$state, ...data, _fetched: true } as any
      }
      catch {} // ignore error
    },
  },
})

export const useRechargeInterceptionStore = defineStore({
  id: 'rechargeInterception',
  state: () => ({
    _exist: false,
    rechargeBlockingActivityId: '',
    rechargeBlockingActivityName: '',
    rechargeBlockingActivityImgUrl: '',
    productId: '',
    productName: '',
    productDescription: '',
    productType: 1,
    prizeId: '',
    prizeName: '',
    cname: '',
    minRechargeAmount: 0,
    redPacketAmount: 0,
    countDown: 0,
    pointerState: 0,
    interceptType: 1, // 减金额 = 1,固定金额 = 2
    style: 1,
    coinsBuy: 0,
    coinsBonus: 0,
    cornerTip: '',
    footTip: '',
    exchangeAmount: 1,
  }),
  actions: {
    async init() {
      const { data } = await api.get<null, any>('/activity/getRechargeBlockingActivityInfo')
      const _exist = Boolean(data)
      this.$state = { ...this.$state, ...data, _exist }
    },
  },
})

// refs: https://doc.weixin.qq.com/sheet/e3_AbAA8Ab1AA0MTQcebGWTIyH9wx7pC?scode=AAsAkAdiAAs002NeV3AbAA8Ab1AA0&tab=lmg39t
const week = 7 * 24 * 60 * 60 * 1000
const month = 30 * 24 * 60 * 60 * 1000
const quarter = 3 * month
export const useTrackValueStore = defineStore({
  id: 'trackValue',
  state: () => {
    const bookId = '0'
    const firstOriginData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_FIRST_ORIGIN_MAP)
    const originNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_ORIGIN_NAME_MAP)
    const chlNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_CHANNEL_NAME_MAP)
    return {
      bookId,
      firstOrigin: firstOriginData?.[bookId]?.firstOrigin ?? 0,
      originName: originNameData?.[bookId]?.originName ?? 0,
      chlName: chlNameData?.[bookId]?.chlName ?? 0,
    }
  },
  actions: {
    set({ bookId = '0', firstOrigin = 0, originName = 0, chlName = 0 }: { bookId?: string; firstOrigin?: number; originName?: number; chlName?: number }) {
      if (bookId === '0') {
        if (firstOrigin && this.firstOrigin === 0)
          this.firstOrigin = firstOrigin

        if (originName && this.originName === 0)
          this.originName = originName

        if (chlName && this.chlName === 0)
          this.chlName = chlName
      }
      else {
        this.bookId = bookId
        const firstOriginData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_FIRST_ORIGIN_MAP)
        const originNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_ORIGIN_NAME_MAP)
        const chlNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_CHANNEL_NAME_MAP)

        if (firstOrigin && !firstOriginData?.[bookId]?.firstOrigin) {
          this.firstOrigin = firstOrigin
          firstOriginData[bookId] = { firstOrigin, expire: Date.now() + quarter }
          localStorage.setItem(STORAGE_TRACK_VALUE_FIRST_ORIGIN_MAP, JSON.stringify(firstOriginData))
        }
        if (originName && !originNameData?.[bookId]?.originName) {
          this.originName = originName
          originNameData[bookId] = { originName, expire: Date.now() + week }
          localStorage.setItem(STORAGE_TRACK_VALUE_ORIGIN_NAME_MAP, JSON.stringify(originNameData))
        }
        if (chlName && !chlNameData?.[bookId]?.chlName) {
          this.chlName = chlName
          chlNameData[bookId] = { chlName, expire: Date.now() + week }
          localStorage.setItem(STORAGE_TRACK_VALUE_CHANNEL_NAME_MAP, JSON.stringify(chlNameData))
        }
      }
    },
    setFromLocal(bookId: string) {
      const firstOriginData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_FIRST_ORIGIN_MAP)
      const originNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_ORIGIN_NAME_MAP)
      const chlNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_CHANNEL_NAME_MAP)

      if (!this.firstOrigin)
        this.firstOrigin = firstOriginData?.[bookId]?.firstOrigin ?? 0
      if (!this.originName)
        this.originName = originNameData?.[bookId]?.originName ?? 0
      if (!this.chlName)
        this.chlName = chlNameData?.[bookId]?.chlName ?? 0
    },
    // 上报之前清洗下数据
    refresh() {
      const firstOriginData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_FIRST_ORIGIN_MAP)
      const originNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_ORIGIN_NAME_MAP)
      const chlNameData = getLocalStorageWithUnexpired(STORAGE_TRACK_VALUE_CHANNEL_NAME_MAP)

      if (!this.firstOrigin) {
        this.firstOrigin = firstOriginData?.['0']?.firstOrigin
        delete firstOriginData?.['0']
      }
      if (!this.originName) {
        this.originName = originNameData?.['0']?.originName
        delete originNameData?.['0']
      }
      if (!this.chlName) {
        this.chlName = chlNameData?.['0']?.chlName
        delete chlNameData?.['0']
      }

      this.set({ bookId: this.bookId, firstOrigin: this.firstOrigin, originName: this.originName, chlName: this.chlName })
      this.bookId = '0'
    },
  },
})

const STORAGE_AUDIO_PLAYER = 'audio_player_state'
const STORAGE_AUDIO_PAYMENT_CHAPTER = 'audio_payment_chapter'
const LS_AUDIO_PAYMENT_CHAPTER = 'ls_audio_payment_chapter'

export const useAudioPlayerStore = defineStore({
  id: 'audioPlayer',
  state: () => ({
    visible: false,
    miniMode: false,
    contentId: '',
    contentName: '',
    contentCoverUrl: '',
    chapterList: [] as GetContentGetcontentchapterbooklistResponse['data']['records'],
    currentChapterId: '',
    currentChapterIndex: -1,
    isPlaying: false,
    playbackRate: 1,
    currentTime: 0,
    duration: 0,
    paymentRequiredChapterId: '',
    audioLoading: false,
    restored: false,
    serialStatus: 0,
    audioElement: null as HTMLAudioElement | null,
  }),
  getters: {
    currentChapter: (state) => {
      return state.chapterList.find(ch => ch.id === state.currentChapterId)
    },
    canPlay: (state) => {
      const chapter = state.chapterList.find(ch => ch.id === state.currentChapterId)
      return !!chapter?.ttsAudioUrl
    },
    hasNextChapter: (state) => {
      return state.currentChapterIndex < state.chapterList.length - 1
    },
    hasPrevChapter: (state) => {
      return state.currentChapterIndex > 0
    },
  },
  actions: {
    initAudio() {
      if (this.audioElement)
        return
      this.audioElement = new Audio()
      window.addEventListener('beforeunload', () => {
        this.saveState()
      })
      this.audioElement.addEventListener('timeupdate', () => {
        this.currentTime = this.audioElement?.currentTime || 0
        this.saveState()
      })
      this.audioElement.addEventListener('loadedmetadata', () => {
        this.duration = this.audioElement?.duration || 0
      })
      this.audioElement.addEventListener('ended', () => {
        this.playNextChapter()
      })
      this.audioElement.addEventListener('waiting', () => {
        this.audioLoading = true
      })
      this.audioElement.addEventListener('canplay', () => {
        this.audioLoading = false
      })
      this.audioElement.addEventListener('playing', () => {
        this.audioLoading = false
      })
      this.audioElement.addEventListener('pause', () => {
        this.audioLoading = false
      })
      this.audioElement.addEventListener('seeking', () => {
        this.audioLoading = true
      })
      this.audioElement.addEventListener('seeked', () => {
        this.audioLoading = false
      })
      this.audioElement.addEventListener('error', () => {
        this.isPlaying = false
        this.audioLoading = false
      })
    },
    async loadChapterList(contentId: string) {
      const { data } = await api.get<any>('/content/getContentChapterBookList', {
        params: { contentId, pageIndex: 1, pageSize: 1500 },
      })
      const newList = data.records || []
      // 保留当前会话中已解锁章节的本地状态，避免目录接口缓存导致已解锁章节回退为锁定
      if (this.contentId === contentId && this.chapterList.length > 0)
        mergeUnlockedChapters(newList, this.chapterList)
      this.chapterList = newList
    },
    async openPlayer(options: {
      contentId: string
      contentName: string
      contentCoverUrl: string
      chapterId: string
      chapterList?: GetContentGetcontentchapterbooklistResponse['data']['records']
    }) {
      const prevContentId = this.contentId
      const contentChanged = !!prevContentId && prevContentId !== options.contentId

      if (contentChanged) {
        if (this.audioElement) {
          this.audioElement.pause()
          this.audioElement.src = ''
        }
        this.isPlaying = false
        this.audioLoading = false
        this.currentTime = 0
        this.duration = 0
        this.currentChapterIndex = -1
        this.currentChapterId = ''
        this.chapterList = []
        this.paymentRequiredChapterId = ''
        this.clearPaymentChapterId()
        this.clearLsPaymentChapterId()
      }

      this.contentId = options.contentId
      this.contentName = options.contentName
      this.contentCoverUrl = options.contentCoverUrl
      this.currentChapterId = options.chapterId
      this.currentChapterIndex = -1
      this.visible = true
      this.miniMode = false

      if (options.chapterList)
        this.chapterList = mergeUnlockedChapters(options.chapterList, this.chapterList)

      else if (this.chapterList.length === 0 || prevContentId !== options.contentId)
        await this.loadChapterList(options.contentId)

      let currentChapterId = options.chapterId
      let currentChapterIndex = this.chapterList.findIndex(ch => ch.id === currentChapterId)

      // 书籍详情的 firstChapter.id 可能与目录接口的章节 id 格式不一致，此时回退到第一条可听章节。
      if (currentChapterIndex === -1) {
        const firstPlayableChapter = this.chapterList.find(ch => !!ch.ttsAudioUrl) || this.chapterList[0]
        if (firstPlayableChapter) {
          currentChapterId = firstPlayableChapter.id
          currentChapterIndex = this.chapterList.findIndex(ch => ch.id === currentChapterId)
        }
      }

      this.currentChapterId = currentChapterId
      this.currentChapterIndex = currentChapterIndex
      this.initAudio()
      await this.playCurrentChapter()
      this.saveState()
    },
    async playCurrentChapter() {
      const chapter = this.currentChapter
      if (!chapter?.ttsAudioUrl || !this.audioElement) {
        this.isPlaying = false
        this.audioLoading = false
        return
      }
      if (this.audioElement.src !== chapter.ttsAudioUrl)
        this.audioElement.src = chapter.ttsAudioUrl
      this.audioElement.playbackRate = this.playbackRate
      this.audioLoading = true
      try {
        await this.audioElement.play()
        this.isPlaying = true
      }
      catch {
        this.isPlaying = false
      }
      finally {
        this.audioLoading = false
      }
    },
    async togglePlay() {
      const chapter = this.currentChapter
      if (chapter && chapter.lockStatus === CHAPTER_LOCK.LOCK) {
        await this.tryUnlockAndPlay(chapter, this.currentChapterIndex)
        return
      }

      if (!this.audioElement) {
        if (!chapter?.ttsAudioUrl) {
          this.audioLoading = false
          return
        }
        this.initAudio()
      }
      if (!this.audioElement) {
        this.audioLoading = false
        return
      }
      const el = this.audioElement
      if (!el.src) {
        const chapter = this.currentChapter
        if (chapter?.ttsAudioUrl) {
          el.src = chapter.ttsAudioUrl
          el.currentTime = this.currentTime
          el.playbackRate = this.playbackRate
        }
      }
      if (this.isPlaying) {
        el.pause()
        this.isPlaying = false
        this.audioLoading = false
      }
      else {
        this.audioLoading = true
        el.play().then(() => {
          this.isPlaying = true
        }).catch(() => {
          this.isPlaying = false
        }).finally(() => {
          this.audioLoading = false
        })
      }
    },
    seek(time: number) {
      if (this.audioElement) {
        this.audioElement.currentTime = time
        this.currentTime = time
      }
    },
    skip(seconds: number) {
      if (this.audioElement) {
        const newTime = Math.max(0, Math.min(this.duration, this.audioElement.currentTime + seconds))
        this.audioElement.currentTime = newTime
        this.currentTime = newTime
      }
    },
    setPlaybackRate(rate: number) {
      this.playbackRate = rate
      if (this.audioElement)
        this.audioElement.playbackRate = rate
    },
    async playNextChapter() {
      if (!this.hasNextChapter)
        return
      const nextIndex = this.currentChapterIndex + 1
      const nextChapter = this.chapterList[nextIndex]
      if (!nextChapter)
        return

      // 已解锁，直接播放
      if (nextChapter.lockStatus !== CHAPTER_LOCK.LOCK && nextChapter.ttsAudioUrl) {
        this.currentChapterIndex = nextIndex
        this.currentChapterId = nextChapter.id
        await this.playCurrentChapter()
        return
      }

      // 未解锁，尝试调用 play 接口解锁
      await this.tryUnlockAndPlay(nextChapter, nextIndex)
    },
    async playPrevChapter() {
      if (!this.hasPrevChapter)
        return
      const prevIndex = this.currentChapterIndex - 1
      const prevChapter = this.chapterList[prevIndex]
      if (!prevChapter)
        return

      if (prevChapter.lockStatus !== CHAPTER_LOCK.LOCK && prevChapter.ttsAudioUrl) {
        this.currentChapterIndex = prevIndex
        this.currentChapterId = prevChapter.id
        await this.playCurrentChapter()
        return
      }

      await this.tryUnlockAndPlay(prevChapter, prevIndex)
    },
    async tryUnlockAndPlay(chapter: any, index: number) {
      try {
        const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
          contentId: this.contentId,
          contentChapterId: chapter.id,
          sequenceNo: 1,
        })
        chapter.lockStatus = CHAPTER_LOCK.UNLOCK
        chapter.ttsAudioUrl = data.ttsAudioUrl
        chapter.bookChapterFileUrl = data.bookChapterFileUrl
        this.currentChapterIndex = index
        this.currentChapterId = chapter.id
        await this.playCurrentChapter()
      }
      catch (error: any) {
        const { code } = error
        if (code === 403) {
          // 先清空再设置，确保 Vue watch 能检测到变化（处理重复进入同一收费章节的情况）
          this.paymentRequiredChapterId = ''
          setTimeout(() => {
            // 如果播放器已被关闭，不再设置
            if (this.audioElement)
              this.paymentRequiredChapterId = chapter.id
          }, 0)
          this.audioElement?.pause()
          this.isPlaying = false
          this.audioLoading = false
          this.saveLsPaymentChapterId(chapter.id)
          this.savePaymentChapterId(chapter.id)
          this.saveState()
        }
        else {
          // 其他错误已在 tryUnlockAndPlay 中处理
        }
      }
    },
    async resumePlayAfterPayment() {
      const chapterId = this.paymentRequiredChapterId
      if (!chapterId)
        return

      this.paymentRequiredChapterId = ''
      // 注意: 不清除 LS/sessionStorage——可能被 PayTemplateSuccess 提前触发,实际支付尚未完成
      const index = this.chapterList.findIndex(ch => ch.id === chapterId)
      const chapter = this.chapterList[index]

      this.initAudio()

      try {
        const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
          contentId: this.contentId,
          contentChapterId: chapterId,
          sequenceNo: 1,
        })
        chapter.lockStatus = CHAPTER_LOCK.UNLOCK
        chapter.ttsAudioUrl = data.ttsAudioUrl
        chapter.bookChapterFileUrl = data.bookChapterFileUrl
        this.currentChapterIndex = index
        this.currentChapterId = chapterId
        this.clearPaymentChapterId()
        this.clearLsPaymentChapterId()
        await this.playCurrentChapter()
      }
      catch (error) {
      }
    },
    selectChapter(chapterId: string) {
      const index = this.chapterList.findIndex(ch => ch.id === chapterId)
      if (index === -1)
        return
      const chapter = this.chapterList[index]
      if (!chapter?.ttsAudioUrl)
        return
      this.currentChapterIndex = index
      this.currentChapterId = chapterId
      this.playCurrentChapter()
      this.saveState()
    },
    minimizePlayer() {
      this.visible = false
      this.miniMode = true
      this.saveState()
    },
    closePlayer() {
      if (this.audioElement) {
        this.audioElement.pause()
        this.audioElement.src = ''
      }
      this.isPlaying = false
      this.audioLoading = false
      this.currentTime = 0
      this.duration = 0
      this.visible = false
      this.miniMode = false
      if (this.paymentRequiredChapterId)

        this.paymentRequiredChapterId = ''
      this.clearPaymentChapterId()
      this.clearLsPaymentChapterId()
      this.clearState()
    },
    savePaymentChapterId(chapterId: string) {
      try {
        sessionStorage.setItem(STORAGE_AUDIO_PAYMENT_CHAPTER, chapterId)
      }
      catch {}
    },
    clearPaymentChapterId() {
      try {
        sessionStorage.removeItem(STORAGE_AUDIO_PAYMENT_CHAPTER)
      }
      catch {}
    },
    restorePaymentChapterId(): string {
      try {
        const val = sessionStorage.getItem(STORAGE_AUDIO_PAYMENT_CHAPTER)
        return val || ''
      }
      catch { return '' }
    },
    saveLsPaymentChapterId(chapterId: string) {
      try {
        localStorage.setItem(LS_AUDIO_PAYMENT_CHAPTER, chapterId)
      }
      catch {}
    },
    restoreLsPaymentChapterId(): string {
      try {
        const val = localStorage.getItem(LS_AUDIO_PAYMENT_CHAPTER)
        return val || ''
      }
      catch { return '' }
    },
    clearLsPaymentChapterId() {
      try {
        localStorage.removeItem(LS_AUDIO_PAYMENT_CHAPTER)
      }
      catch {}
    },
    saveState() {
      try {
        const data = {
          contentId: this.contentId,
          contentName: this.contentName,
          contentCoverUrl: this.contentCoverUrl,
          currentChapterId: this.currentChapterId,
          currentChapterIndex: this.currentChapterIndex,
          currentTime: this.currentTime,
          playbackRate: this.playbackRate,
          chapterList: this.chapterList,
          paymentRequiredChapterId: this.paymentRequiredChapterId,
          serialStatus: this.serialStatus,
        }
        sessionStorage.setItem(STORAGE_AUDIO_PLAYER, JSON.stringify(data))
      }
      catch (e) {
      }
    },
    restoreState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_AUDIO_PLAYER)
        if (!raw)
          return false
        const saved = JSON.parse(raw)
        this.contentId = saved.contentId
        this.contentName = saved.contentName || ''
        this.contentCoverUrl = saved.contentCoverUrl || ''
        this.currentChapterId = saved.currentChapterId || ''
        this.currentChapterIndex = saved.currentChapterIndex ?? -1
        this.currentTime = saved.currentTime || 0
        this.playbackRate = saved.playbackRate || 1
        this.chapterList = saved.chapterList || []
        this.paymentRequiredChapterId = saved.paymentRequiredChapterId || this.restorePaymentChapterId() || this.restoreLsPaymentChapterId()
        this.serialStatus = saved.serialStatus ?? 0
        this.restored = true
        return true
      }
      catch { return false }
    },
    clearState() {
      try {
        sessionStorage.removeItem(STORAGE_AUDIO_PLAYER)
        sessionStorage.removeItem(STORAGE_AUDIO_PAYMENT_CHAPTER)
        this.clearLsPaymentChapterId()
      }
      catch {}
    },
  },
})

/**
 * 注销账号时清理听书播放器状态，避免切换账号后保留上一个用户的播放进度。
 */
function closeAudioPlayerOnLogout() {
  useAudioPlayerStore().closePlayer()
}

/**
 * 将 sourceList 中已解锁章节的状态合并到 targetList，用于避免目录接口缓存覆盖本地解锁状态。
 */
function mergeUnlockedChapters(targetList: any[], sourceList: any[]) {
  if (!sourceList?.length)
    return targetList

  const unlockedMap = new Map(
    sourceList
      .filter(ch => ch.lockStatus === CHAPTER_LOCK.UNLOCK)
      .map(ch => [ch.id, ch]),
  )

  for (const item of targetList) {
    if (item.lockStatus !== CHAPTER_LOCK.LOCK)
      continue
    const unlocked = unlockedMap.get(item.id)
    if (!unlocked)
      continue
    item.lockStatus = CHAPTER_LOCK.UNLOCK
    if (unlocked.bookChapterFileUrl && !item.bookChapterFileUrl)
      item.bookChapterFileUrl = unlocked.bookChapterFileUrl
    if (unlocked.bookChapterFreeFileUrl && !item.bookChapterFreeFileUrl)
      item.bookChapterFreeFileUrl = unlocked.bookChapterFreeFileUrl
    if (unlocked.ttsAudioUrl && !item.ttsAudioUrl)
      item.ttsAudioUrl = unlocked.ttsAudioUrl
  }

  return targetList
}

/**
 * 将听书播放器中已解锁的章节状态同步到指定的章节列表。
 * 用于解决听书解锁后返回阅读页/目录页时，因目录接口缓存导致仍显示锁定的问题。
 */
export function syncChapterListWithAudioStore(contentId: string, chapterList: any[]) {
  const audioStore = useAudioPlayerStore()
  if (!audioStore.contentId || audioStore.contentId !== contentId || !audioStore.chapterList?.length)
    return

  mergeUnlockedChapters(chapterList, audioStore.chapterList)
}

// JSON stringify data with `expire` field
function getLocalStorageWithUnexpired(key: string) {
  let value: undefined | { [key: string]: any }
  try {
    const data = JSON.parse(localStorage.getItem(key) ?? '{}')
    for (const bookId of Object.keys(data)) {
      const item = data[bookId]
      if (item.expire && item.expire < Date.now())
        delete data[bookId]
    }
    return data
  }
  catch {}
  return value
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
  import.meta.hot.accept(acceptHMRUpdate(useActivityStore, import.meta.hot))
}
